/**
 * Converts a snake_case string to camelCase
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-zA-Z0-9])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Converts a camelCase string to snake_case
 */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Recursively converts all keys in an object from snake_case to camelCase
 */
export function keysToCamelCase<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => keysToCamelCase(item)) as T;
  }

  if (typeof obj === "object" && obj.constructor === Object) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = keysToCamelCase(obj[key]);
      return acc;
    }, {} as any) as T;
  }

  return obj;
}

/**
 * Recursively converts all keys in an object from camelCase to snake_case
 */
export function keysToSnakeCase<T = any>(obj: any): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => keysToSnakeCase(item)) as T;
  }

  if (typeof obj === "object" && obj.constructor === Object) {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = toSnakeCase(key);
      acc[snakeKey] = keysToSnakeCase(obj[key]);
      return acc;
    }, {} as any) as T;
  }

  return obj;
}

/**
 * Converts an object to FormData recursively
 * Handles nested arrays, and File/Blob objects with standard FormData naming.
 * NOTE: Nested plain objects (not arrays, files, or blobs) are converted to a JSON string.
 *
 * @param obj - The object to convert
 * @param formData - FormData instance (optional, created if not provided)
 * @param prefix - Current key prefix for nested values
 * @returns FormData instance
 *
 * @example
 * const data = {
 * name: 'John',
 * details: { age: 30, city: 'NY' }, // details will be stringified
 * files: [fileObject1, fileObject2], // files will use standard indexing
 * };
 * const formData = objectToFormData(data);
 */
export function objectToFormData(
  obj: any,
  formData = new FormData(),
  prefix = "",
): FormData {
  if (obj === null || obj === undefined) {
    return formData;
  }

  // 1. Handle File/Blob objects: append them directly.
  if (obj instanceof File || obj instanceof Blob) {
    formData.append(prefix, obj);
    return formData;
  }

  // 2. Handle Arrays: iterate and use 'prefix[index]' keys. Recurse for nested items.
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const key = `${prefix}[${index}]`;
      if (
        typeof item === "object" &&
        !(item instanceof File || item instanceof Blob)
      ) {
        // Recurse for nested objects/arrays within an array
        objectToFormData(item, formData, key);
      } else if (item !== null && item !== undefined) {
        // Append primitive or file/blob items
        formData.append(key, item);
      }
    });
    return formData;
  }

  // *** MODIFICATION STARTS HERE ***

  // 3. Handle plain objects (including the root object if no prefix is set).
  if (typeof obj === "object") {
    // If a prefix exists, stringify the entire nested object and append it.
    // This is the core change to convert nested objects to a JSON string.
    if (prefix) {
      formData.append(prefix, JSON.stringify(obj));
      return formData;
    }

    // If no prefix exists (the root level object), iterate its keys and recurse/append.
    // This ensures root properties are processed.
    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[key];
        const newPrefix = prefix ? `${prefix}[${key}]` : key;

        if (
          typeof value === "object" &&
          !(value instanceof File || value instanceof Blob)
        ) {
          // Recurse for nested objects/arrays (the next level will be stringified if it's a plain object)
          objectToFormData(value, formData, newPrefix);
        } else if (value !== null && value !== undefined) {
          // Append primitive or file/blob items
          formData.append(newPrefix, value);
        }
      }
    }
    return formData;
  }

  // 4. Handle primitives at the root (if the original call wasn't an object)
  // This case shouldn't typically happen if the input is always an object,
  // but is kept for completeness in the original structure logic.
  if (prefix) {
    formData.append(prefix, obj);
  }

  return formData;
}
